import * as React from "react"
import { Tooltip as TooltipPrimitive } from "radix-ui"

import { cn } from "@/lib/utils"
import { useTabActive } from "@/hooks/useTabVisit"
import {
  FLOATER_ARROW_HEIGHT,
  FLOATER_ARROW_PADDING,
  FLOATER_ARROW_WIDTH,
  FloaterArrowShape,
  floaterEdgeClass,
} from "./floater-arrow"

function TooltipProvider({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  )
}

function Tooltip({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return (
    <TooltipProvider>
      <TooltipPrimitive.Root data-slot="tooltip" {...props} />
    </TooltipProvider>
  )
}

function TooltipTrigger({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />
}

function TooltipContent({
  className,
  sideOffset = 4,
  arrowPadding = FLOATER_ARROW_PADDING,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
  // Drops the floater while its kept-alive tab is hidden (see useTabActive).
  const tabActive = useTabActive()
  if (!tabActive) return null
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        arrowPadding={arrowPadding}
        className={cn(
          "bg-popover text-popover-foreground floater-motion origin-(--radix-tooltip-content-transform-origin) z-[80] w-fit max-w-xs rounded-md px-3 py-1.5 text-xs",
          floaterEdgeClass,
          className
        )}
        {...props}
      >
        {children}
        <TooltipPrimitive.Arrow asChild width={FLOATER_ARROW_WIDTH} height={FLOATER_ARROW_HEIGHT}>
          <FloaterArrowShape />
        </TooltipPrimitive.Arrow>
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  )
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
